import { EventEmitter, Injectable, Output } from '@angular/core';
import * as internal from 'events';
import { BehaviorSubject } from 'rxjs';
import { CNHealthProblem } from '../models/cnhealthproblem';
import { CNPatient } from '../models/cnpatient';
import { SNItemFactory } from '../models/snitemfactory';
import { BecareService } from './becare.service';
import { PatientService } from './patient.service';

export enum ProblemServiceFilter {
  all = 'all',
  active = 'active',
  notactive = 'notactive'
}

export class ProblemServiceSubject {
  private becareService: BecareService;
  private myPatient: CNPatient;
  private myName: string;
  private myFilter: ProblemServiceFilter;
  private paged: boolean;
  private buffer: CNHealthProblem[];
  public problems: BehaviorSubject<CNHealthProblem[]>;
  public myStart: number;
  public myLimit: number;
  public length: number;

  private problemUrl = '';

  constructor(becare: BecareService, patient: CNPatient, name: string, filter: ProblemServiceFilter, paged: boolean) {
    this.becareService = becare;
    this.myName = name;
    this.myFilter = filter;
    this.paged = paged;
    if (paged) {
      this.myStart = 0;
      this.myLimit = 10;
      this.length = 0;
    }
    this.problems = new BehaviorSubject<CNHealthProblem[]>([]);
    // Set patient, build rest url and query first buffer
    this.patient = patient;
  }

  public load() {
    if (!this.problemUrl) {
      return;
    }
    this.becareService.call(this.problemUrl).subscribe(result => {
      if (result.success === true && result.problems !== undefined) {
        const factory = new SNItemFactory();
        this.buffer = factory.itemsFromJson(result.problems);
        this.length = result.total;
        this.problems.next(this.buffer);
      }
    });
  }

  private buildUrl() {
    if (this.paged) {
      this.problemUrl = '/fr/patient/' + this.myPatient.id + '/problems?start=' + this.myStart + '&limit=' + this.myLimit;
    } else {
      switch (this.myFilter) {
        case ProblemServiceFilter.active:
          this.problemUrl = '/fr/patient/' + this.myPatient.id + '/problems/active';
          break;
        case ProblemServiceFilter.notactive:
          this.problemUrl = '/fr/patient/' + this.myPatient.id + '/problems/notactive';
          break;
        default:
          this.problemUrl = '';
      }
    }
  }

  set patient(patient: CNPatient) {
    this.myPatient = patient;
    if (!this.myPatient) {
      return;
    }
    this.buildUrl();
    this.load();
  }

  set start(start: number) {
    this.myStart = start;
    this.buildUrl();
    this.load();
  }

  set limit(limit: number) {
    this.myLimit = limit;
    this.buildUrl();
    this.load();
  }
}

@Injectable({
  providedIn: 'root'
})
export class ProblemService {

  private static instance: ProblemService = null;
  private currentpatient: CNPatient;
  @Output() clear = new EventEmitter<boolean>();

  private subjects: Map<string, ProblemServiceSubject> = new Map();

  constructor(private becare: BecareService, private patient: PatientService) {
    ProblemService.instance = this;
    this.patient.patientSelectedEvent.subscribe(patientselected => {
      this.currentpatient = patientselected;
      this.subjects.forEach((subject: ProblemServiceSubject, name: string) => {
        subject.patient = this.currentpatient;
      });
      this.clear.emit(true);
    });
  }

  public static getInstance(): ProblemService {
    return ProblemService.instance;
  }

  public getSubject(name: string, filter: ProblemServiceFilter, paged: boolean): ProblemServiceSubject {
    if (this.subjects.has(name)) {
      return this.subjects.get(name);
    }
    // Build new service subject
    const newSubject = new ProblemServiceSubject(this.becare, this.currentpatient, name, filter, paged);
    this.subjects.set(name, newSubject);
    return newSubject;
  }

  public reloadAllSubjects() {
    this.subjects.forEach((value, key) => {
      value.load();
    });
  }
}
